// Colophon — shared end-of-story footer.
//
// Extracted from public/src/components/viewer/Colophon.tsx into
// @scrolly/viewer so both app/ and public/ consumers can render the
// canonical "End." footer via the renderFooter slot.
//
// Two visual variants:
//   - ColophonSubtle (default): newspaper back matter, BG #f7f5ef
//   - ColophonBold: magazine back cover, BG #0a0a0a, Mega-"End." in Pink

import { MarkdownViewer } from "@scrolly/renderer";
import { t, type Lang } from "./i18n";
import { resolveColophonSources, type QuellenEintrag } from "@scrolly/post-types";
import { PoweredByScrollyBand } from "./PoweredByScrolly";

const PINK = "#FF3366";

// Marke im Abbinder: Grotesk der Oberflaeche, nicht die Serifenschrift der
// Ueberschriften (Jans Vorgabe 05.08.2026).
const BRAND_FONT =
    'var(--scrolly-font-ui, "DM Sans", -apple-system, system-ui, sans-serif)';

// Die Zieladresse steht an ZWEI Stellen im Abbinder (schlicht und kraeftig) —
// als Konstante, damit der naechste Domain-Wechsel nicht wieder eine davon
// vergisst.
const SCROLLY_DOMAIN = "scrolly.com";
const SCROLLY_URL = `https://${SCROLLY_DOMAIN}`;

export type ColophonStyle = "subtle" | "bold";

export interface ColophonLegalLinks {
  imprintUrl?: string;
  privacyUrl?: string;
  accessibilityUrl?: string;
  address?: string;
}

export interface ColophonOrgContact {
  name: string;
  email?: string;
}

export interface ColophonProps {
  creatorName: string;
  publishedAt?: string;
  /**
   * Veroeffentlichungsdatum als ISO-Wert (Jan 21.09.2026): steht als
   * "Veroeffentlicht am TT.MM.JJJJ" VOR den Credits, mit Trennlinie darunter.
   */
  publishedIso?: string | null;
  duration?: string;
  views?: string;
  credits?: string;
  sources?: string;
  contact?: string;
  orgContact?: ColophonOrgContact | null;
  legalLinks?: ColophonLegalLinks | null;
  wide?: boolean;
  isCompact?: boolean;
  style?: ColophonStyle;
  lang?: Lang;
  /**
   * "powered by scrolly" zeigen (Band + Bold-Abbinder-Markenzeile).
   * Default true. False nur, wenn die Org es abschalten DARF
   * (branding.white_label via Plan oder Superadmin-planOverride) und
   * abgeschaltet HAT — der Server erzwingt das am Auslieferungspunkt
   * (user-scrollys.service, Badge-Zwang 27.08.2026). Rechtslinks
   * (Impressum/Datenschutz/(c)) bleiben davon unberuehrt.
   */
  showPoweredBy?: boolean;
  /**
   * KI-Kennzeichnung (KI-Verordnung, Jans Auftrag 01.09.2026): Hinweis
   * unter Credits & Quellen. Erscheint in ALLEN Abbinder-Varianten —
   * auch im White-Label-Rechts-Footer, die Kennzeichnung ist Pflicht,
   * kein Branding.
   */
  aiDisclosure?: boolean;
}

/**
 * Quellen: Zerlegen in Links bzw. freien Text liegt in @scrolly/post-types
 * (colophon-sources) — dieselbe Regel braucht auch die Editor-Vorschau, und
 * getestet ist sie dort ebenfalls. Frueher stand hier ein Parser, der nur am
 * Trennzeichen „ • " zerlegte und je Stueck den ERSTEN Link nahm: Drei
 * untereinander getippte Quellen erschienen so als eine (Jans Fund
 * 05.08.2026).
 */
type SourceEntry = QuellenEintrag;

export function Colophon({
  creatorName,
  publishedAt,
  publishedIso,
  duration,
  views,
  credits,
  sources: sourcesMd,
  contact: contactMd,
  orgContact,
  legalLinks,
  wide = false,
  isCompact = false,
  style = "subtle",
  lang = 'de',
  showPoweredBy = true,
  aiDisclosure = false,
}: ColophonProps) {
  const quellen = resolveColophonSources(sourcesMd);
  const parsedSources = quellen.links;
  /**
   * FREIER TEXT als Quellenangabe (Jans Vorgabe 05.08.2026: die Quellen sind
   * ein normales Textfeld). Wer keine `[Titel](url)`-Links tippt, hatte bisher
   * nichts im Abbinder stehen — die Angabe verschwand kommentarlos. Regel und
   * Begruendung in colophon-sources.
   */
  const quellenText = quellen.text;
  const creditsTrimmed = credits?.trim() ?? "";
  const contactTrimmed = contactMd?.trim() ?? "";
  const published = publishedAt || "";

  const narrow = isCompact || !wide;

  if (style === "bold") {
    return (
      <ColophonBold
        creatorName={creatorName}
        wide={wide}
        narrow={narrow}
        sources={parsedSources}
        quellenText={quellenText}
        creditsMd={creditsTrimmed}
        contactMd={contactTrimmed}
        published={published}
        publishedIso={publishedIso}
        duration={duration}
        views={views}
        orgContact={orgContact}
        legalLinks={legalLinks}
        lang={lang}
        showPoweredBy={showPoweredBy}
        aiDisclosure={aiDisclosure}
      />
    );
  }
  return (
    <ColophonSubtle
      creatorName={creatorName}
      wide={wide}
      narrow={narrow}
      sources={parsedSources}
      quellenText={quellenText}
      creditsMd={creditsTrimmed}
      contactMd={contactTrimmed}
      published={published}
      publishedIso={publishedIso}
      duration={duration}
      views={views}
      orgContact={orgContact}
      legalLinks={legalLinks}
      lang={lang}
      showPoweredBy={showPoweredBy}
      aiDisclosure={aiDisclosure}
    />
  );
}

/** TT.MM.JJJJ (de) bzw. "Aug 26, 2022" (en); leer bei ungueltigem Wert. */
export function formatVeroeffentlichtAm(iso: string | null | undefined, lang: Lang = 'de'): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (lang === 'en') {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const zz = (n: number) => String(n).padStart(2, "0");
  return `${zz(d.getDate())}.${zz(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/**
 * "Veroeffentlicht am TT.MM.JJJJ" — erste Zeile des Abbinders, VOR den
 * Credits, mit Trennlinie darunter (Jans Vorgabe 21.09.2026). Gleiche Optik
 * wie die Spalten-Labels der Credits.
 */
function VeroeffentlichtZeile({ iso, lang, narrow }: { iso?: string | null; lang: Lang; narrow: boolean }) {
  const datum = formatVeroeffentlichtAm(iso, lang);
  if (!datum) return null;
  return (
    <div
      data-testid="colophon-veroeffentlicht"
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        color: "#7a766b",
        padding: narrow ? "0 0 20px" : "0 0 24px",
        marginBottom: narrow ? 24 : 32,
        borderBottom: "1px solid #e3e0d5",
      }}
    >
      {t('colophon.publishedOn', lang)} {datum}
    </div>
  );
}

interface SharedSubProps {
  creatorName: string;
  wide: boolean;
  narrow: boolean;
  sources: SourceEntry[];
  /** Quellenangabe als freier Text (nur wenn keine Markdown-Links vorliegen). */
  quellenText: string;
  creditsMd: string;
  contactMd: string;
  published: string;
  publishedIso?: string | null;
  duration?: string;
  views?: string;
  orgContact?: ColophonOrgContact | null;
  legalLinks?: ColophonLegalLinks | null;
  lang: Lang;
  showPoweredBy: boolean;
  aiDisclosure?: boolean;
}

/**
 * KI-Hinweis (KI-Verordnung): kleiner "AI"-Kreis + ein Satz, unterhalb von
 * Credits & Quellen. Bewusst dieselbe Optik in allen Abbinder-Varianten.
 */
function AiDisclosureNote({
  lang,
  narrow,
  bordered = true,
  dark = false,
}: {
  lang: Lang;
  narrow: boolean;
  bordered?: boolean;
  /** Bold-Abbinder ist dunkel — Kreis/Text invertiert. */
  dark?: boolean;
}) {
  return (
    <div
      data-testid="colophon-ai-disclosure"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: narrow ? "20px 0" : "24px 0",
        borderBottom: bordered
          ? `1px solid ${dark ? "rgba(255,255,255,0.12)" : "#e3e0d5"}`
          : "none",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: dark ? "#f7f5ef" : "#1a1a1a",
          color: dark ? "#1a1a1a" : "#f7f5ef",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.5,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        AI
      </span>
      <span
        style={{
          fontSize: 13.5,
          lineHeight: 1.5,
          color: dark ? "rgba(255,255,255,0.75)" : "#4a463c",
        }}
      >
        {t('colophon.aiDisclosure', lang)}
      </span>
    </div>
  );
}

function ColophonSubtle({
  wide,
  narrow,
  publishedIso,
  sources,
  quellenText,
  creditsMd,
  contactMd,
  orgContact,
  legalLinks,
  lang,
  showPoweredBy,
  aiDisclosure,
}: SharedSubProps) {
  const p = wide && !narrow ? "100px 60px 60px" : "60px 24px 36px";
  const grid2Col = wide && !narrow ? "140px 1fr" : "1fr";
  const longStringStyle: React.CSSProperties = narrow
    ? { overflowWrap: "anywhere", wordBreak: "break-word" }
    : {};
  const hasCredits = creditsMd.length > 0;
  const hasSources = sources.length > 0 || quellenText.length > 0;
  const address = legalLinks?.address;
  const hasContactSection =
    !!orgContact?.name || !!orgContact?.email || contactMd.length > 0;

  return (
    <div
      data-testid="colophon-subtle"
      style={{
        background: "#f7f5ef",
        color: "#1a1a1a",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          maxWidth: wide ? 820 : "100%",
          margin: wide ? "0 auto" : 0,
          padding: p,
          boxSizing: "border-box",
        }}
      >
        {/* Der kurze dicke Deko-Strich stand hier — auf Kundenwunsch entfernt
            (via Benedikt/Jan 27.08.2026), in ALLEN Abbinder-Varianten. */}

        {/* Byline („Von …" + Veroeffentlicht-Zeile) BEWUSST WEG (Jans
            Sichttest 26.08.2026): Vorgabe ist der schlanke Viewer-Abbinder —
            Credits/Quellen, Kontakt, Rechtslinks, dunkles Band. */}

        <VeroeffentlichtZeile iso={publishedIso} lang={lang} narrow={narrow} />

        {/* Credits */}
        {hasCredits && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid2Col,
              gap: narrow ? 20 : 40,
              padding: narrow ? "0 0 24px" : "0 0 32px",
              borderBottom: "1px solid #e3e0d5",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                paddingTop: 4,
              }}
            >
              {t('colophon.credits', lang)}
            </div>
            <div
              className="colophon-markdown"
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: "#1a1a1a",
                ...longStringStyle,
              }}
            >
              <MarkdownViewer source={creditsMd} />
            </div>
          </div>
        )}

        {/* Sources */}
        {hasSources && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid2Col,
              gap: narrow ? 20 : 40,
              padding: narrow ? "24px 0" : "32px 0",
              borderBottom: "1px solid #e3e0d5",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                paddingTop: 4,
              }}
            >
              {t('colophon.sources', lang)}
            </div>
            {/* Freier Text (keine Markdown-Links) — in derselben Optik wie die
                Credits, damit eine getippte Quellenangabe nicht verschwindet. */}
            {quellenText && (
              <div
                className="colophon-markdown"
                style={{
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: "#1a1a1a",
                  ...longStringStyle,
                }}
              >
                <MarkdownViewer source={quellenText} />
              </div>
            )}
            <ol
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {sources.map((s, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    gap: 14,
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--scrolly-font-display, 'Playfair Display', Georgia, serif)",
                      fontSize: 15,
                      color: PINK,
                      fontStyle: "italic",
                      width: 20,
                      flexShrink: 0,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span style={{ minWidth: 0, ...longStringStyle }}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1a1a1a",
                        textDecoration: "none",
                        borderBottom: "1px solid transparent",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.borderBottomColor = "currentColor")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.borderBottomColor = "transparent")
                      }
                    >
                      {s.label}
                    </a>
                    <span style={{ color: "#9a968a", marginLeft: 8 }}>
                      {s.url}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* KI-Kennzeichnung — direkt unter Credits & Quellen (Mock Jans,
            01.09.2026). */}
        {aiDisclosure && <AiDisclosureNote lang={lang} narrow={narrow} />}

        {/* Contact — ohne Unterlinie: zwischen Kontakt und den Rechtslinks
            standen zwei Linien mit Leerraum (diese + die Oberlinie des
            Abbinders), im Viewer gibt es dort keine (Jans Sichttest
            26.08.2026). */}
        {hasContactSection && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid2Col,
              gap: narrow ? 20 : 40,
              padding: narrow ? "24px 0" : "32px 0",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                paddingTop: 4,
              }}
            >
              {t('colophon.contact', lang)}
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: "#1a1a1a",
                minWidth: 0,
                ...longStringStyle,
              }}
            >
              {orgContact?.name && (
                <div style={{ marginBottom: 6, ...longStringStyle }}>
                  <strong>{orgContact.name}</strong>
                </div>
              )}
              {address && (
                <div
                  style={{
                    color: "#6b6760",
                    marginBottom: 6,
                    ...longStringStyle,
                  }}
                >
                  {address.split(",").map((part, i, arr) => (
                    <span key={i}>
                      {part.trim()}
                      {i < arr.length - 1 && <br />}
                    </span>
                  ))}
                </div>
              )}
              {orgContact?.email && (
                <div
                  style={{
                    color: "#6b6760",
                    marginBottom: 6,
                    ...longStringStyle,
                  }}
                >
                  <a
                    href={`mailto:${orgContact.email}`}
                    style={{
                      color: "#1a1a1a",
                      textDecoration: "underline",
                      ...longStringStyle,
                    }}
                  >
                    {orgContact.email}
                  </a>
                </div>
              )}
              {!orgContact?.name && contactMd && (
                <div className="colophon-markdown" style={longStringStyle}>
                  <MarkdownViewer source={contactMd} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Abbinder — direkt unter dem Kontakt, ohne eigene Linie. */}
        <div
          style={{
            marginTop: 8,
            paddingTop: 16,
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          {/* Markenzeile ersetzt durch das dunkle powered-by-Band ganz unten
              (Jans Screenshot 26.08.2026) — hier bleiben Adresse + Rechtslinks. */}
          <AbbinderLinks legalLinks={legalLinks} color="#9a968a" hoverColor="#1a1a1a" lang={lang} />
        </div>
      </div>
      {showPoweredBy && <PoweredByScrollyBand />}
    </div>
  );
}

/**
 * ColophonLegalFooter — reduzierte End-Sequenz.
 *
 * Wird gerendert, wenn das Org-Flag `badges.showColophon === false` gesetzt ist
 * (z. B. WESER-KURIER). VORHER hat dieses Flag die komplette End-Slide via
 * `return null` entfernt — damit verschwand aber auch das gesetzlich
 * verpflichtende Impressum/Datenschutz, was den standalone erreichbaren Viewer
 * (viewer.scrolly.com/scrolly/…) rechtlich angreifbar machte (Bene 2026-08-07).
 *
 * Bleibt: schlanke Rechts-Fußzeile — Herausgeber + Adresse + Impressum/
 * Datenschutz-Links, plus — falls im Scrolly gepflegt — Bildnachweise/Quellen
 * (Urheberbenennung, §13 UrhG).
 * Bewusst WEG (= Lauras Wunsch „Kontakt- und Credit-Slide ausblenden"): Byline/
 * „End."-Karte, Kontakt-Block, „powered by scrolly"-Markenzeile.
 */
export function ColophonLegalFooter({
  orgContact,
  legalLinks,
  publishedIso,
  credits,
  sources: sourcesMd,
  wide = false,
  isCompact = false,
  lang = 'de',
  fontFamily,
  showPoweredBy = true,
  aiDisclosure = false,
}: {
  orgContact?: ColophonOrgContact | null;
  legalLinks?: ColophonLegalLinks | null;
  /** s. ColophonProps.publishedIso */
  publishedIso?: string | null;
  credits?: string;
  sources?: string;
  wide?: boolean;
  isCompact?: boolean;
  lang?: Lang;
  /**
   * Org-Branding-Schrift (Fließtext-Familie, z. B. Weser Sans). Wird auf die
   * ganze Fußzeile gelegt, damit Herausgeber/Adresse/Links in der Marken-
   * schrift stehen statt in der generischen UI-Schrift. Fallback: inherit.
   */
  fontFamily?: string;
  /** s. ColophonProps.showPoweredBy — Server erzwingt die Erlaubnis. */
  showPoweredBy?: boolean;
  /** s. ColophonProps.aiDisclosure — Pflicht-Kennzeichnung, kein Branding. */
  aiDisclosure?: boolean;
}) {
  const quellen = resolveColophonSources(sourcesMd);
  const parsedSources = quellen.links;
  const quellenText = quellen.text;
  const creditsMd = credits?.trim() ?? "";
  const narrow = isCompact || !wide;
  const p = wide && !narrow ? "56px 60px 56px" : "40px 24px 36px";
  const longStringStyle: React.CSSProperties = narrow
    ? { overflowWrap: "anywhere", wordBreak: "break-word" }
    : {};
  const hasCredits = creditsMd.length > 0;
  const hasSources = parsedSources.length > 0 || quellenText.length > 0;
  const hasBackMatter = hasCredits || hasSources;
  const address = legalLinks?.address;

  return (
    <div
      data-testid="colophon-legal"
      style={{
        background: "#f7f5ef",
        color: "#1a1a1a",
        fontFamily: fontFamily || "inherit",
      }}
    >
      <div style={{ maxWidth: wide ? 820 : "100%", margin: wide ? "0 auto" : 0, padding: p, boxSizing: "border-box" }}>
        {/* Deko-Strich entfernt (Kundenwunsch via Benedikt/Jan 27.08.2026). */}

        <VeroeffentlichtZeile iso={publishedIso} lang={lang} narrow={narrow} />

        {/* Bildnachweise — nur falls gepflegt (Urheberbenennung bleibt) */}
        {hasCredits && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                marginBottom: 8,
              }}
            >
              {t('colophon.credits', lang)}
            </div>
            <div
              className="colophon-markdown"
              style={{ fontSize: 14, lineHeight: 1.55, color: "#1a1a1a", ...longStringStyle }}
            >
              <MarkdownViewer source={creditsMd} />
            </div>
          </div>
        )}

        {/* Quellen — nur falls gepflegt */}
        {hasSources && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                marginBottom: 8,
              }}
            >
              {t('colophon.sources', lang)}
            </div>
            {quellenText && (
              <div
                className="colophon-markdown"
                style={{ fontSize: 14, lineHeight: 1.55, color: "#1a1a1a", ...longStringStyle }}
              >
                <MarkdownViewer source={quellenText} />
              </div>
            )}
            {parsedSources.length > 0 && (
              <ol
                style={{
                  margin: quellenText ? "10px 0 0" : 0,
                  padding: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {parsedSources.map((s, i) => (
                  <li
                    key={i}
                    style={{ display: "flex", gap: 14, fontSize: 14, lineHeight: 1.5 }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--scrolly-font-display, 'Playfair Display', Georgia, serif)",
                        fontSize: 15,
                        color: PINK,
                        fontStyle: "italic",
                        width: 20,
                        flexShrink: 0,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span style={{ minWidth: 0, ...longStringStyle }}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1a1a1a", textDecoration: "none" }}
                      >
                        {s.label}
                      </a>
                      <span style={{ color: "#9a968a", marginLeft: 8 }}>{s.url}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* KI-Kennzeichnung — Pflicht, bleibt auch im White-Label (Mock
            Jans, 01.09.2026). */}
        {aiDisclosure && (
          <div style={{ marginBottom: 24 }}>
            <AiDisclosureNote lang={lang} narrow={narrow} bordered={false} />
          </div>
        )}

        {/* Rechts-Zeile: Herausgeber + Adresse links, Impressum/Datenschutz rechts */}
        <div
          style={{
            marginTop: hasBackMatter ? 12 : 0,
            paddingTop: 24,
            borderTop: "1px solid #e3e0d5",
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b6760", ...longStringStyle }}>
            {orgContact?.name && (
              <strong style={{ color: "#1a1a1a" }}>{orgContact.name}</strong>
            )}
            {orgContact?.name && address ? " · " : null}
            {address ? <span>{address}</span> : null}
          </div>
          <AbbinderLinks
            legalLinks={legalLinks}
            color="#9a968a"
            hoverColor="#1a1a1a"
            lang={lang}
          />
        </div>
      </div>
      {showPoweredBy && <PoweredByScrollyBand />}
    </div>
  );
}

// „AGB" stand hier mal als dritter Eintrag — am 26.08.2026 auf Benedikts
// Ansage überall entfernt (Impressum + Datenschutz + © reichen).
function AbbinderLinks({
  legalLinks,
  color,
  hoverColor,
  lang,
}: {
  legalLinks?: ColophonLegalLinks | null;
  color: string;
  hoverColor: string;
  lang: Lang;
}) {
  const linkStyle: React.CSSProperties = {
    color,
    textDecoration: "none",
    transition: "color 120ms ease-out",
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        fontSize: 11.5,
        color,
      }}
    >
      {legalLinks?.imprintUrl ? (
        <a
          href={legalLinks.imprintUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.color = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = color)}
        >
          {t('colophon.imprint', lang)}
        </a>
      ) : (
        <span>{t('colophon.imprint', lang)}</span>
      )}
      {legalLinks?.privacyUrl ? (
        <a
          href={legalLinks.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.color = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = color)}
        >
          {t('colophon.privacy', lang)}
        </a>
      ) : (
        <span>{t('colophon.privacy', lang)}</span>
      )}
      <span>© 2026</span>
    </div>
  );
}

function ColophonBold({
  creatorName,
  narrow,
  sources,
  quellenText,
  creditsMd,
  contactMd,
  published,
  duration,
  views,
  orgContact,
  legalLinks,
  lang,
  showPoweredBy,
  aiDisclosure,
}: SharedSubProps) {
  const p = narrow ? "72px 24px 36px" : "120px 60px 56px";
  const longStringStyle: React.CSSProperties = narrow
    ? { overflowWrap: "anywhere", wordBreak: "break-word" }
    : {};
  const hasCredits = creditsMd.length > 0;
  const hasSources = sources.length > 0 || quellenText.length > 0;
  const address = legalLinks?.address;
  const hasContact =
    !!orgContact?.name || !!orgContact?.email || contactMd.length > 0;

  return (
    <div
      data-testid="colophon-bold"
      style={{
        background: "#0a0a0a",
        color: "#fff",
        padding: p,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Separator line (replaces "End." word — no language problem) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: narrow ? "0 0 50px" : "0 0 80px",
        }}
      >
        <div
          style={{
            width: 64,
            height: 4,
            borderRadius: 2,
            background: PINK,
          }}
        />
      </div>

      <div style={{ maxWidth: narrow ? "100%" : 960, margin: "0 auto" }}>
        {/* Two-column editorial block */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: !narrow && hasCredits ? "1fr 1fr" : "1fr",
            gap: narrow ? 36 : 60,
            paddingBottom: narrow ? 32 : 48,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 14,
              }}
            >
              {t('colophon.theByline', lang)}
            </div>
            <div
              style={{
                fontFamily: "var(--scrolly-font-display, 'Playfair Display', Georgia, serif)",
                fontSize: narrow ? 30 : 44,
                letterSpacing: -0.8,
                lineHeight: 1.05,
                ...longStringStyle,
              }}
            >
              {creatorName}
            </div>
            <div
              style={{
                fontSize: 13.5,
                color: "rgba(255,255,255,0.55)",
                marginTop: 12,
                lineHeight: 1.55,
              }}
            >
              {published ? <>{t('colophon.published', lang)} {published}</> : null}
              {published && duration ? " · " : null}
              {duration || null}
              {views ? (
                <>
                  <br />
                  {t('colophon.readBy', lang)} {views} {t('colophon.people', lang)}
                </>
              ) : null}
            </div>
          </div>
          {hasCredits && (
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: PINK,
                  marginBottom: 14,
                }}
              >
                {t('colophon.credits', lang)}
              </div>
              <div
                className="colophon-markdown"
                style={{
                  fontSize: 14.5,
                  lineHeight: 1.55,
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                <MarkdownViewer source={creditsMd} />
              </div>
            </div>
          )}
        </div>

        {/* Sources */}
        {hasSources && (
          <div
            style={{
              padding: narrow ? "32px 0" : "48px 0",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 18,
              }}
            >
              {t('colophon.sources', lang)}
            </div>
            {/* Freier Text (keine Markdown-Links) — s. Kommentar in der
                schlichten Variante. */}
            {quellenText && (
              <div
                className="colophon-markdown"
                style={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "rgba(255,255,255,0.72)",
                  marginBottom: 18,
                  ...longStringStyle,
                }}
              >
                <MarkdownViewer source={quellenText} />
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: narrow ? "1fr" : "repeat(3, 1fr)",
                gap: narrow ? 18 : 32,
              }}
            >
              {sources.map((s, i) => (
                <div
                  key={i}
                  style={{
                    borderTop: "1px solid rgba(255,255,255,0.15)",
                    paddingTop: 12,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--scrolly-font-display, 'Playfair Display', Georgia, serif)",
                      fontSize: 22,
                      color: PINK,
                      fontStyle: "italic",
                      lineHeight: 1,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      fontSize: 14,
                      color: "#fff",
                      marginTop: 8,
                      lineHeight: 1.35,
                      textDecoration: "none",
                      ...longStringStyle,
                    }}
                  >
                    {s.label}
                  </a>
                  <div
                    style={{
                      fontSize: 12,
                      color: "rgba(255,255,255,0.45)",
                      marginTop: 4,
                      ...longStringStyle,
                    }}
                  >
                    {s.url}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KI-Kennzeichnung (Mock Jans, 01.09.2026) */}
        {aiDisclosure && <AiDisclosureNote lang={lang} narrow={narrow} dark />}

        {/* Contact */}
        {hasContact && (
          <div
            style={{
              padding: narrow ? "28px 0" : "40px 0",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              fontSize: 14,
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 14,
              }}
            >
              {t('colophon.contact', lang)}
            </div>
            {orgContact?.name && (
              <div style={{ color: "#fff", marginBottom: 6, ...longStringStyle }}>
                <strong>{orgContact.name}</strong>
              </div>
            )}
            {address && (
              <div
                style={{
                  color: "rgba(255,255,255,0.55)",
                  marginBottom: 6,
                  ...longStringStyle,
                }}
              >
                {address.split(",").map((part, i, arr) => (
                  <span key={i}>
                    {part.trim()}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </div>
            )}
            {orgContact?.email && (
              <div
                style={{
                  color: "rgba(255,255,255,0.55)",
                  marginBottom: 6,
                  ...longStringStyle,
                }}
              >
                <a
                  href={`mailto:${orgContact.email}`}
                  style={{
                    color: "#fff",
                    textDecoration: "underline",
                    ...longStringStyle,
                  }}
                >
                  {orgContact.email}
                </a>
              </div>
            )}
            {!orgContact?.name && contactMd && (
              <div className="colophon-markdown" style={longStringStyle}>
                <MarkdownViewer source={contactMd} />
              </div>
            )}
          </div>
        )}

        {/* Abbinder as pull quote — Markenzeile + Eigenwerbung nur mit
            showPoweredBy (Badge-Zwang 27.08.2026); die Rechtslinks
            (BoldAbbinderLinks) bleiben IMMER. */}
        <div
          style={{
            padding: narrow ? "44px 0 16px" : "64px 0 16px",
            textAlign: "center",
          }}
        >
          {showPoweredBy && (
            <>
              <div
                style={{
                  fontFamily: BRAND_FONT,
                  fontSize: narrow ? 24 : 36,
                  lineHeight: 1.2,
                  letterSpacing: -0.4,
                  fontStyle: "italic",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                powered by{" "}
                <span style={{ color: PINK, letterSpacing: -0.8 }}>scrolly</span>
              </div>
              <div
                style={{
                  marginTop: 14,
                  fontSize: 13,
                  color: "rgba(255,255,255,0.5)",
                  letterSpacing: 0.3,
                }}
              >
                {t('colophon.publishOwnStory', lang)}{" "}
                <a
                  href={SCROLLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#fff",
                    borderBottom: "1px solid rgba(255,255,255,0.3)",
                    textDecoration: "none",
                  }}
                >
                  {SCROLLY_DOMAIN}
                </a>
              </div>
            </>
          )}
          <BoldAbbinderLinks legalLinks={legalLinks} wide={!narrow} lang={lang} />
        </div>
      </div>
    </div>
  );
}

function BoldAbbinderLinks({
  legalLinks,
  wide,
  lang,
}: {
  legalLinks?: ColophonLegalLinks | null;
  wide: boolean;
  lang: Lang;
}) {
  const linkBase: React.CSSProperties = {
    color: "rgba(255,255,255,0.55)",
    textDecoration: "none",
    transition: "color 120ms ease-out",
  };
  const linkHover = "#fff";
  return (
    <div
      style={{
        marginTop: wide ? 40 : 28,
        display: "flex",
        gap: 14,
        justifyContent: "center",
        flexWrap: "wrap",
        fontSize: 11,
        color: "rgba(255,255,255,0.35)",
        letterSpacing: 0.4,
      }}
    >
      {legalLinks?.imprintUrl ? (
        <a
          href={legalLinks.imprintUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkBase}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = linkBase.color as string)
          }
        >
          {t('colophon.imprint', lang)}
        </a>
      ) : (
        <span>{t('colophon.imprint', lang)}</span>
      )}
      <span>·</span>
      {legalLinks?.privacyUrl ? (
        <a
          href={legalLinks.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkBase}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = linkBase.color as string)
          }
        >
          {t('colophon.privacy', lang)}
        </a>
      ) : (
        <span>{t('colophon.privacy', lang)}</span>
      )}
      <span>·</span>
      <span>© 2026 Scrolly</span>
    </div>
  );
}

export default Colophon;
